import {createAction, handleActions} from 'redux-actions';
import {Api} from '../../api/api';
import {fetchSubmitForm, fetchSubmitFormFailed, fetchSubmitFormSuccess} from '../common/submitForm';
import { logout, onForbidden } from '../account/login';
import _ from 'lodash';

export const PENDING = 'PENDING';
export const GRANTED = 'GRANTED';
export const REJECTED = 'REJECTED';
export const REVOKED = 'REVOKED';

//- Actions
export const onRequestCredentialsVerification = createAction(
    'FETCH_REQUEST_CREDENTIALS_VERIFICATION',
    (data) => Api.requestCredentialsVerification(data),
    () => ({
        apiCall: true,
        onSuccess: fetchSubmitFormSuccess,
        onFail: fetchSubmitFormFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const onRequestBulkCredentialsVerification = createAction(
    'FETCH_REQUEST_BULK_CREDENTIALS_VERIFICATION',
    (data) => Api.requestBulkCredentialsVerification(data),
    () => ({
        apiCall: true,
        onSuccess: fetchSubmitFormSuccess,
        onFail: onRequestBulkCredentialsVerificationFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);
export const onRequestBulkCredentialsVerificationFailed = (error) => (dispatch) => {
    switch (error) {
        case "REQUEST_BODY_INVALID":
            dispatch(fetchSubmitFormFailed("INCORRECT_FILE_CONTENT"));
            break;

        default:
            dispatch(fetchSubmitFormFailed(error));
            break;
    }
};

export const fetchRequestCredentialsVerification = (data) => dispatch => {
    dispatch(fetchSubmitForm());
    dispatch(onRequestCredentialsVerification(data));
};

export const fetchRequestBulkCredentialsVerification = (data) => dispatch => {
    dispatch(fetchSubmitForm());
    dispatch(onRequestBulkCredentialsVerification(data));
};

export const setCurrentStatus = createAction('CURRENT_STATUS_SET');

export const getRequestsByStatus = (status, page) => dispatch => {
    dispatch(setCurrentStatus(status));
    dispatch(fetchGetRequestsByStatus(status, page));
};

export const onGetConsentsSuccess = (data) => (dispatch, getState) => {
    let isEmpty = data.empty;
    let currentPage = data.number;
    if ((!isEmpty && currentPage !== 0) || currentPage === 0) {
        dispatch(fetchGetRequestsSuccess(data));
    } else {
        dispatch(getRequestsByStatus(getCurrentStatus(getState()), currentPage - 1));
    }
};

export const fetchGetRequestsFailed = createAction('FETCH_GET_REQUESTS_FAILED');
export const fetchGetRequestsSuccess = createAction('FETCH_GET_REQUESTS_SUCCESS');
export const fetchGetRequestsByStatus = createAction(
    'FETCH_GET_REQUESTS_BY_STATUS',
    (status, page) => Api.getRequestsByStatus(status, page),
    () => ({
        apiCall: true,
        onSuccess: onGetConsentsSuccess,
        onFail: fetchGetRequestsFailed,
        onUnauthorized: logout,
        onForbidden: onForbidden
    })
);

export const onSetNextConsent = () => (dispatch, getState) => {
    let list = getConsentsData(getState());
    let currentPage = getCurrentPageForConsents(getState());
    dispatch(incrementNumberOfSkippedConsents());

    if (!_.isNil(list) && list.length > 0) {
        let currentIndex = getCurrentConsentIndex(getState());
        if (currentIndex < list.length - 1) {
            dispatch(setCurrentConsent(currentIndex + 1));
            dispatch(getRequestsByStatus(PENDING, currentPage));
        } else {
            let totalPages = getTotalPagesForConsents(getState());
            if (currentPage < totalPages - 1) {
                dispatch(setCurrentConsent(0));
                dispatch(getRequestsByStatus(PENDING, currentPage + 1));
            } else {
                dispatch(setCurrentConsent(null));
            }
        }
    }
};

export const incrementNumberOfProcessedConsents = createAction('NUMBER_OF_PROCESSED_CONSENTS_INCREMENT');
export const incrementNumberOfSkippedConsents = createAction('NUMBER_OF_SKIPPED_CONSENTS_INCREMENT');
export const setNumberOfSkippedConsents = createAction('NUMBER_OF_SKIPPED_CONSENTS_SET');

export const setCurrentConsent = createAction('CURRENT_CONSENT_SET', index => index);
export const startConsentsProcessing = (id) => (dispatch, getState) => {
    let index = getConsentIndexById(getState(), id);
    let currentPage = getCurrentPageForConsents(getState());
    let skipped = (currentPage * 10) + index;
    dispatch(setNumberOfSkippedConsents(skipped));
    dispatch(setCurrentConsent(index));
    dispatch(cleanProcessingStatus());
};
export const cleanProcessingStatus = createAction('PROCESSING_STATUS_CLEAN');

//- State
const initialState = {
    currentStatus: null,
    data: null,
    error: null,
    fetching: false,
    currentPage: null,
    totalPages: null,
    currentConsentIndex: null,
    processed: 0,
    leftNumber: null,
    skipped: 0
};

//- Reducers
export default handleActions({
    FETCH_GET_REQUESTS_BY_STATUS: (state) => {
        return {...state, data: null, error: null, fetching: true, leftNumber: null, totalPages: null, currentPage: null,};
    },
    FETCH_GET_REQUESTS_FAILED: (state, action) => {
        return {...state, data: null, error: action.payload, fetching: false, currentPage: null, totalPages: null};
    },
    FETCH_GET_REQUESTS_SUCCESS: (state, action) => {
        return {...state, data: action.payload.content, error: null, fetching: false, leftNumber: action.payload.totalElements, currentPage: action.payload.number, totalPages: action.payload.totalPages};
    },
    CURRENT_STATUS_SET: (state, action) => {
        return {...state, currentStatus: action.payload};
    },
    CURRENT_CONSENT_SET: (state, action) => {
        return {...state, currentConsentIndex: action.payload};
    },
    PROCESSING_STATUS_CLEAN: (state) => {
        return {...state, processed: 0};
    },
    NUMBER_OF_SKIPPED_CONSENTS_SET: (state, action) => {
        return {...state, skipped: action.payload};
    },
    NUMBER_OF_SKIPPED_CONSENTS_INCREMENT: (state) => {
        return {...state, skipped: state.skipped + 1};
    },
    NUMBER_OF_PROCESSED_CONSENTS_INCREMENT: (state) => {
        return {...state, processed: state.processed + 1};
    },
}, initialState);

//- Selectors
export const getConsents = state => state.consents;

export const loadingConsents = state => getConsents(state).fetching;

export const getConsentsData = state => getConsents(state).data;

export const getConsentsList = state => {
    return _.map(getConsentsData(state), consent => ({
        id: consent.id,
        firstName: consent.requester ? consent.requester.identityAlias.firstName : null,
        lastName: consent.requester ? consent.requester.identityAlias.lastName : null,
        companyName: consent.organization.name,
        countryCode: consent.organization.address.countryCode,
        email: consent.contactEmail,
        expireDate: consent.grantedTill,
        jobTitle: consent.jobDescription.title,
        requestDate: consent.createdAt
    }))
};

export const getCurrentStatus = state => getConsents(state).currentStatus;

export const getCurrentPageForConsents = state => getConsents(state).currentPage;

export const getTotalPagesForConsents = state => getConsents(state).totalPages;

export const getCurrentConsentIndex = state => getConsents(state).currentConsentIndex;

export const getConsentIndexById = (state, id) => {
    let data = getConsentsData(state);

    if (_.isNil(data)) {
        return null;
    }

    let index = _.findIndex(data, consent => consent.id === id);

    if (index < 0) {
        return null;
    }

    return index;
};

export const getLeftConsentsNumber = state => getConsents(state).leftNumber;

export const getNumberOfSkippedConsents = state => getConsents(state).skipped;

export const getNumberOfProcessedConsents = state => getConsents(state).processed;

export const getCurrentConsentId = state => {
    let index = getCurrentConsentIndex(state);
    let ids = _.map(getConsentsData(state), org => org.id);

    if (index >= ids.length) {
        return null;
    }
    return ids[index];
};


// WEBPACK FOOTER //
// ./src/reducers/consent/consents.js